import { PrescriptionSettings } from '../types/settings.types';

const SETTINGS_STORAGE_KEY = 'prescription_settings';
const SETTINGS_BACKUP_KEY = 'prescription_settings_backup';

/**
 * Save settings to localStorage as backup
 */
export const saveSettingsToLocal = (settings: PrescriptionSettings): void => {
  try {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    localStorage.setItem(SETTINGS_BACKUP_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('Error saving settings to localStorage:', error);
  }
};

/**
 * Get settings from localStorage
 */
export const getSettingsFromLocal = (): PrescriptionSettings | null => {
  try {
    const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as PrescriptionSettings;
    }
  } catch (error) {
    console.error('Error reading settings from localStorage:', error);
  }
  return null;
};

/**
 * Get backup settings from localStorage
 */
export const getBackupSettingsFromLocal = (): PrescriptionSettings | null => {
  try {
    const stored = localStorage.getItem(SETTINGS_BACKUP_KEY);
    if (stored) {
      return JSON.parse(stored) as PrescriptionSettings;
    }
  } catch (error) {
    console.error('Error reading backup settings from localStorage:', error);
  }
  return null;
};

/**
 * Clear settings from localStorage
 */
export const clearLocalSettings = (): void => {
  try {
    localStorage.removeItem(SETTINGS_STORAGE_KEY);
    localStorage.removeItem(SETTINGS_BACKUP_KEY);
  } catch (error) {
    console.error('Error clearing settings from localStorage:', error);
  }
};

/**
 * Export settings as PDF file
 */
export const exportSettings = async (settings: PrescriptionSettings): Promise<void> => {
  try {
    // Dynamic import to avoid loading jsPDF if not needed
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF();
    
    // Set font
    doc.setFont('helvetica');
    
    // Title
    doc.setFontSize(18);
    doc.text('إعدادات الوصفة الطبية / Prescription Settings', 105, 20, { align: 'center' });
    
    let yPos = 35;
    const lineHeight = 8;
    const margin = 20;
    const pageHeight = doc.internal.pageSize.height;
    
    // Helper function to add text with page break
    const addText = (label: string, value: string | number | boolean | undefined) => {
      if (yPos > pageHeight - 20) {
        doc.addPage();
        yPos = 20;
      }
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`${label}:`, margin, yPos);
      doc.setFont('helvetica', 'normal');
      const valueStr = value !== undefined && value !== null ? String(value) : 'N/A';
      doc.text(valueStr, margin + 60, yPos);
      yPos += lineHeight;
    };
    
    // Clinic Information
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('معلومات العيادة / Clinic Information', margin, yPos);
    yPos += lineHeight + 2;
    
    addText('اسم العيادة / Clinic Name', settings.clinicName);
    addText('عنوان العيادة / Address', settings.clinicAddress || '');
    addText('رقم الهاتف / Phone', settings.clinicPhone || '');
    addText('اسم الطبيب / Doctor Name', settings.doctorName);
    
    yPos += 5;
    
    // Paper Settings
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('إعدادات الورق / Paper Settings', margin, yPos);
    yPos += lineHeight + 2;
    
    addText('عرض الورقة (مم) / Width (mm)', settings.paperWidthMM);
    addText('ارتفاع الورقة (مم) / Height (mm)', settings.paperHeightMM);
    addText('الهامش العلوي (مم) / Top Margin (mm)', settings.marginTop);
    addText('الهامش السفلي (مم) / Bottom Margin (mm)', settings.marginBottom);
    addText('الهامش الأيسر (مم) / Left Margin (mm)', settings.marginLeft);
    addText('الهامش الأيمن (مم) / Right Margin (mm)', settings.marginRight);
    
    yPos += 5;
    
    // Font Settings
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('إعدادات الخط / Font Settings', margin, yPos);
    yPos += lineHeight + 2;
    
    addText('اسم الخط / Font Name', settings.fontName);
    addText('حجم الخط / Font Size', settings.fontSize);
    
    yPos += 5;
    
    // Print Settings
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('إعدادات الطباعة / Print Settings', margin, yPos);
    yPos += lineHeight + 2;
    
    addText('نوع الطباعة / Print Type', settings.printType);
    addText('اسم الطابعة الافتراضية / Default Printer', settings.defaultPrinterName || '');
    addText('الطباعة مع الشعار / Print With Logo', settings.printWithLogo ? 'نعم / Yes' : 'لا / No');
    addText('رابط الشعار / Logo URL', settings.logoUrl || '');
    addText('الطباعة التلقائية / Auto Print', settings.autoPrint ? 'نعم / Yes' : 'لا / No');
    
    yPos += 5;
    
    // Display Settings
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('إعدادات العرض / Display Settings', margin, yPos);
    yPos += lineHeight + 2;
    
    addText('إظهار الرأس / Show Header', settings.showHeader ? 'نعم / Yes' : 'لا / No');
    addText('إظهار التشخيص / Show Diagnosis', settings.showDiagnosis ? 'نعم / Yes' : 'لا / No');
    addText('إظهار التذييل / Show Footer', settings.showFooter ? 'نعم / Yes' : 'لا / No');
    addText('إظهار QR Code / Show QR', settings.showQR ? 'نعم / Yes' : 'لا / No');
    
    // Footer
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      doc.text(
        `تم التصدير في / Exported on: ${new Date().toLocaleDateString('ar-EG', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        })} - الصفحة / Page ${i} من / of ${totalPages}`,
        105,
        pageHeight - 10,
        { align: 'center' }
      );
    }
    
    // Save PDF
    const fileName = `prescription_settings_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  } catch (error) {
    console.error('Error exporting settings as PDF:', error);
    throw error;
  }
};

/**
 * Import settings from JSON or PDF file
 * For PDF, we'll extract embedded JSON if available, otherwise fallback to JSON
 */
export const importSettings = async (file: File): Promise<PrescriptionSettings> => {
  return new Promise((resolve, reject) => {
    // Check file type
    if (file.type === 'application/pdf') {
      // For PDF, try to extract JSON from metadata or use a simple approach
      // Since extracting from PDF is complex, we'll use a fallback:
      // Try to read as text first (some PDFs have embedded JSON)
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const content = e.target?.result as string;
          // Try to find JSON in the PDF content
          const jsonMatch = content.match(/\{[\s\S]*"clinicName"[\s\S]*\}/);
          if (jsonMatch) {
            const settings = JSON.parse(jsonMatch[0]) as PrescriptionSettings;
            if (settings.clinicName && settings.doctorName) {
              resolve(settings);
              return;
            }
          }
          // If no JSON found, reject with helpful message
          reject(new Error('PDF does not contain readable settings. Please use JSON format for import.'));
        } catch (error) {
          reject(new Error('Failed to extract settings from PDF. Please use JSON format for import.'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read PDF file'));
      reader.readAsText(file);
    } else {
      // For JSON files
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;
          const settings = JSON.parse(content) as PrescriptionSettings;
          // Validate settings structure
          if (!settings.clinicName || !settings.doctorName) {
            reject(new Error('Invalid settings file format'));
            return;
          }
          resolve(settings);
        } catch (error) {
          reject(new Error('Failed to parse settings file'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    }
  });
};

